import React, { Dispatch, FunctionComponent, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Formik } from 'formik';
import { LocalizeContextProps, withLocalize } from 'react-localize-redux';
import { usePlaidLink, PlaidLinkOnSuccessMetadata, PlaidLinkOptions } from 'react-plaid-link';
import styled from 'styled-components';
import * as Yup from 'yup';
import useSWRMutation from 'swr/mutation';
import { KeyedMutator } from 'swr';

import { Button, Loading, Text, useLocalize } from '@tradingblock/components';
import { AchRelationship, AchRelationshipApprovalMethods, ApiResponse, CreateAchRelationshipRequest } from '@tradingblock/types';
import { useData } from '../../../state/selectors';
import { DefaultApplicationModel } from '../../../types';
import { useApi } from '../../../context';
import config from '../../../config';
import PlaidAchRelationshipForm from './PlaidAchRelationshipForm';
import { FundYourAccountView, FundYourAccountViews } from '../FundYourAccount';

const StyledPlaidAchRelationship = styled.div`
  .click-text {
    margin-top: 1.5rem;
  }

  .loading-container {
    width: 100%;
    height: 100%
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .open-plaid-btn-wrapper {
    display: flex;
    align-items: center;
  }

  .open-plaid-btn-wrapper button {
    width: 100%;
    margin-bottom: 1rem;
  }

  .progress-stepper-navigation {
    margin-top: 0 !important;
  }
`;

interface PlaidAchRelationshipProps extends LocalizeContextProps {
  plaidLinkToken: string | null;
  getAchRelationshipsData: KeyedMutator<ApiResponse<AchRelationship[]> | null>;
  setCurrentFundYourAccountView: Dispatch<SetStateAction<FundYourAccountView>>;
};

export interface FormValues {
  accountNickname: string;
};

const PlaidAchRelationship: FunctionComponent<PlaidAchRelationshipProps> = ({
  plaidLinkToken,
  translate,
  getAchRelationshipsData,
  setCurrentFundYourAccountView,
}) => {
  const [plaidLinkOnSuccessMetadata, setPlaidLinkOnSuccessMetadata] = useState<PlaidLinkOnSuccessMetadata | null>(null);
  const [plaidFailed, setPlaidFailed] = useState<boolean | null>(null);
  const [plaidSucceeded, setPlaidSucceeded] = useState<boolean>(false);
  const [plaidPublicToken, setPlaidPublicToken] = useState<string | null>(null);
  const [errorSubmittingFundingMethodRequest, setErrorSubmittingFundingMethodRequest] = useState<boolean>(false);
  const [accountLinkingViaPlaidSuccessful, setAccountLinkingViaPlaidSuccessful] = useState<boolean>(false);

  const api = useApi();

  const application = useData(d => d.data.application);

  const { accountId } = application || DefaultApplicationModel;

  const { getTranslationId } = useLocalize();

  const {
    trigger: triggerCreateAchRelationship,
    error: errorCreatingAchRelationship,
    isMutating: isCreatingAchRelationship,
  } = useSWRMutation(
    `${config.tradingApi}/Cashiering/${accountId}/AchRelationships`,
    (_url: string, { arg } : { arg: { accountId: number, createAchRelationshipRequestPayload: CreateAchRelationshipRequest } }) =>
      api.cashiering.ach.create(arg.accountId, arg.createAchRelationshipRequestPayload)
  );
  
  const plaidLinkOptions: PlaidLinkOptions = useMemo(() => {
    return {
      token: plaidLinkToken,
      linkCustomizationName: 'config_en',
      onExit: (error) => {
        if (error) {
          setPlaidFailed(true);
        }
      },
      onSuccess: (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
        setPlaidPublicToken(null);
        setPlaidLinkOnSuccessMetadata(null);
        setPlaidPublicToken(publicToken);
        setPlaidLinkOnSuccessMetadata(metadata);
        setPlaidSucceeded(true);
      },
    } as PlaidLinkOptions;
  }, [plaidLinkToken]);

  const { open: openPlaidLink, error: plaidError, ready: plaidReady } = usePlaidLink(plaidLinkOptions);

  const {
    accountName,
    accountNumberMask,
    bankName,
    accountType,
    accountSubType,
    plaidAccountId,
  }: {
    accountName: string | null;
    accountNumberMask: string | null;
    bankName: string | null;
    accountType: string | null;
    accountSubType: string | null;
    plaidAccountId: string | null;
  } = useMemo(() => {
    if (plaidLinkOnSuccessMetadata) {
      return {
        accountName: (plaidLinkOnSuccessMetadata.accounts && plaidLinkOnSuccessMetadata.accounts[0] && plaidLinkOnSuccessMetadata.accounts[0].name) || null,
        accountNumberMask: (plaidLinkOnSuccessMetadata.accounts && plaidLinkOnSuccessMetadata.accounts[0] && plaidLinkOnSuccessMetadata.accounts[0].mask) || null,
        accountType: (plaidLinkOnSuccessMetadata.accounts && plaidLinkOnSuccessMetadata.accounts[0] && plaidLinkOnSuccessMetadata.accounts[0].type) || null,
        accountSubType: (plaidLinkOnSuccessMetadata.accounts && plaidLinkOnSuccessMetadata.accounts[0] && plaidLinkOnSuccessMetadata.accounts[0].subtype) || null,
        bankName: (plaidLinkOnSuccessMetadata.institution && plaidLinkOnSuccessMetadata.institution.name) || null,
        plaidAccountId: (plaidLinkOnSuccessMetadata.accounts && plaidLinkOnSuccessMetadata.accounts[0] && plaidLinkOnSuccessMetadata.accounts[0].id) || null,
      }
    }

    return { accountName: null, accountNumberMask: null, bankName: null, accountType: null, accountSubType: null, plaidAccountId: null };
  }, [plaidLinkOnSuccessMetadata]);

  const onSubmit = async (values: FormValues) => {    
    if (accountId && plaidAccountId && plaidPublicToken) {
      setErrorSubmittingFundingMethodRequest(false);

      const {
        responseCode: createAchRelationshipResponseCode,
      } = await triggerCreateAchRelationship(
        {
          accountId,
          createAchRelationshipRequestPayload:
          {
            nickName: values.accountNickname,
            plaidAccountId,
            plaidPublicToken,
            approvalMethod: AchRelationshipApprovalMethods.Plaid
          }
        }
      ) as ApiResponse<AchRelationship>;

      if (createAchRelationshipResponseCode === 0) {
        getAchRelationshipsData();
      }

      if (createAchRelationshipResponseCode !== 0) {
        setErrorSubmittingFundingMethodRequest(true);
      }
    }
  };

  useEffect(() => {
    if (errorCreatingAchRelationship) {
      setErrorSubmittingFundingMethodRequest(true);
    }
  }, [errorCreatingAchRelationship]);

  useEffect(() => {
    setAccountLinkingViaPlaidSuccessful(
      !!(
        plaidSucceeded &&
        plaidPublicToken &&
        plaidLinkOnSuccessMetadata &&
        (
          (accountSubType && accountType) &&
          (
            accountType === 'depository' &&
            (
              accountSubType === 'checking' ||
              accountSubType === 'savings'
            )
          )
        )
      )
    )
  }, [
    plaidSucceeded,
    plaidPublicToken,
    plaidLinkOnSuccessMetadata,
    accountSubType,
    accountType
  ]);

  return (
    <StyledPlaidAchRelationship>
      {!plaidReady || isCreatingAchRelationship && <div className="loading-container"><Loading /></div>}

      {
        (
          (
            plaidSucceeded &&
            (
              (!accountSubType || !accountType) ||
              accountType !== 'depository' ||
              (
                accountType === 'depository' &&
                accountSubType !== 'checking' &&
                accountSubType !== 'savings'
              )
            )
          ) ||
          plaidError ||
          plaidFailed ||
          (
            plaidSucceeded &&
            (
              !plaidPublicToken ||
              !plaidLinkOnSuccessMetadata
            )
          )
        ) && (
          <div className="d-flex justify-content-center align-items-center flex-direction-col">
            <i className="fal fa-exclamation-circle error m-1" />
            <p>
              <Text id="fundYourAccountComponent.accountTypeCouldNotBeLinked" type="error" />
            </p>
          </div>
        )
      }

      {
        accountLinkingViaPlaidSuccessful && !isCreatingAchRelationship && (
          <>
            <p>
              <Text
                id="fundYourAccountComponent.accountLinkingViaPlaidSuccessful"
                data={{
                  bankAccountInfo: `${bankName || (`${translate(getTranslationId({ id: 'fundYourAccountComponent.bank' }))} ${translate(getTranslationId({ id: 'fundYourAccountComponent.account' }))}`)}${accountName ? ` ${accountName}` : ''}${accountName && accountNumberMask ? ` (***${accountNumberMask})` : ''}`
                }}
              />
            </p>

            <Formik
              validateOnChange
              validationSchema={
                Yup
                .object<FormValues>()
                .shape({
                  accountNickname: Yup.string().trim().required(translate(getTranslationId({ id: 'defaults.required', type: 'error' })) as string)
                })
              }
              onSubmit={onSubmit}
              initialValues={{ accountNickname: accountName || '' } as FormValues}
            >
              <PlaidAchRelationshipForm
                errorSubmittingFundingMethodRequest={errorSubmittingFundingMethodRequest}
                accountName={accountName}
              />
            </Formik>
          </>
        )
      }

      {plaidReady && !isCreatingAchRelationship && (
        <div className="open-plaid-btn-wrapper">
          <Button className="btn btn-primary" onClick={() => openPlaidLink()}>
            {!accountLinkingViaPlaidSuccessful && <Text id="btns.linkYourBankAccount" />}
            {accountLinkingViaPlaidSuccessful && <Text id="btns.cancelThisRequestAndLinkDifferentBankAccount" />}
          </Button>
        </div>
      )}

      {!isCreatingAchRelationship && (
        <div className="progress-stepper-navigation">
          <Button
            className="btn-link btn-link-solo"
            onClick={() => setCurrentFundYourAccountView(FundYourAccountViews.FundingMethods)}
          >
            <Text id="btns.backToFundingMethods" />
          </Button>
        </div>
      )}
    </StyledPlaidAchRelationship>
  );
};

export default withLocalize(PlaidAchRelationship);